import { ElasticCollection } from "@usereactify/search";
import React, { useContext, useMemo } from "react";
import { ResultStatsProvider } from "../context/ResultStatsContext";
import { ViewSettingsContext, ViewSettingsProvider } from "../context/ViewSettingsContext";
import { CollectionCustomFields, ColorGroup } from "../reactify";
import { Filters } from "./Filters/Filters";
import { Results } from "./Results/Results";

export type CollectionProps = {
  collection: ElasticCollection;
  colorGroups: ColorGroup[] | null;
  collectionCustomFields: CollectionCustomFields;
};

export const Collection: React.FC<CollectionProps> = (props) => {
  const memoizedColorGroups = useMemo(() => props.colorGroups, [props.colorGroups]);

  return (
    <div>
      <ViewSettingsProvider>
        <ResultStatsProvider>
          <CollectionInner colorGroups={memoizedColorGroups} />
        </ResultStatsProvider>
      </ViewSettingsProvider>
    </div>
  );
};

const CollectionInner: React.FC<{ colorGroups: ColorGroup[] | null }> = ({ colorGroups }) => {
  const viewSettings = useContext(ViewSettingsContext);
  if (!viewSettings) throw new Error("ViewSettingsContext must be used within a ViewSettingsProvider");

  const { isSidebarOpen, setSidebarOpen } = viewSettings;

  return (
    <div className="collection-layout">
      <aside className={`collection-sidebar-panel small--hide ${isSidebarOpen ? "is-open" : "is-closed"}`}>
        <div className="collection-sidebar-panel__header">
          <span>Filters</span>
          <button
            className="collection-sidebar-panel__close"
            onClick={() => setSidebarOpen(false)}
            title="Collapse filters"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.04249 9.00044L3.2697 13.7732L4.22429 14.7278L8.99709 9.95504L13.7701 14.728L14.7247 13.7734L9.95168 9.00044L14.7248 4.2273L13.7702 3.27271L8.99709 8.04585L4.22413 3.27289L3.26953 4.22748L8.04249 9.00044Z"
                fill="#2D2926"
              />
            </svg>
          </button>
        </div>
        <div className="collection-sidebar-panel__content">
          <Filters mobile={false} />
        </div>
      </aside>

      <div className={`collection-main ${isSidebarOpen ? "has-sidebar" : "no-sidebar"}`}>
        <div className="collection-filter__item collection-filter__item--left medium-up--hide w-full">
          <Filters mobile={true} />
        </div>

        {!isSidebarOpen && (
          <button className="collection-sidebar-toggle small--hide" onClick={() => setSidebarOpen(true)}>
            <svg width="21" height="16" viewBox="0 0 21 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M11.4383 2.99995C11.8082 1.56233 13.1133 0.5 14.6666 0.5C16.2197 0.5 17.5248 1.56233 17.8948 2.99995H19.6659C20.1265 2.99995 20.4998 3.36976 20.4998 3.83321C20.4998 4.29345 20.1269 4.66662 19.6659 4.66662H17.8948C17.5248 6.10424 16.2197 7.16657 14.6666 7.16657C13.1133 7.16657 11.8083 6.10424 11.4383 4.66662H1.33384C0.873313 4.66662 0.5 4.29667 0.5 3.83321C0.5 3.37297 0.872885 2.99995 1.33384 2.99995H11.4383ZM3.10523 11.333C3.47519 9.89537 4.78026 8.83303 6.33341 8.83303C7.88666 8.83303 9.19174 9.89537 9.56173 11.333H19.6662C20.1267 11.333 20.5 11.7029 20.5 12.1664C20.5 12.6266 20.1271 12.9997 19.6662 12.9997H9.56173C9.19178 14.4373 7.8867 15.4996 6.33341 15.4996C4.7803 15.4996 3.47522 14.4373 3.10523 12.9997H1.33405C0.873528 12.9997 0.500214 12.6298 0.500214 12.1664C0.500214 11.7062 0.873099 11.333 1.33405 11.333H3.10523ZM6.33341 13.8329C7.25385 13.8329 8.00009 13.0868 8.00009 12.1664C8.00009 11.2459 7.25385 10.4997 6.33341 10.4997C5.41297 10.4997 4.66688 11.2459 4.66688 12.1664C4.66688 13.0868 5.41297 13.8329 6.33341 13.8329ZM14.6668 5.49989C15.5872 5.49989 16.3333 4.75366 16.3333 3.83321C16.3333 2.91277 15.5872 2.16668 14.6668 2.16668C13.7464 2.16668 13.0001 2.91277 13.0001 3.83321C13.0001 4.75366 13.7464 5.49989 14.6668 5.49989Z"
                fill="#2D2926"
              />
            </svg>
            Filters
          </button>
        )}

        <Results colorGroups={colorGroups} pageSize={40} />
      </div>
    </div>
  );
};
